import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  calendarApi,
  EVENT_TYPE_KEYS,
  type CalendarEvent,
  type LiveLesson,
  type PlatformEventInput,
  type PlatformEventType,
} from '@/api/calendar';
import { attendanceApi } from '@/api/attendance';
import { curatorApi, usersApi } from '@/api/users';
import { isFeatureOn } from '@/lib/appConfig';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { humanizeError } from '@/lib/errors';
import { apiErrorCode } from '@/lib/apiErrorCode';
import {
  audienceOptions,
  audienceRefusalKey,
  canSaveEvent,
  effectiveGroupId,
  initialAudience,
  type EventAudience,
} from '@/lib/eventAudience';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MIcon, MRow, MSegmented, MTag, MTitle } from '@/components/mobile/MobileKit';

function useLessonStatusLabels() {
  const { t } = useTranslation();
  return {
    scheduled: t('curatorSchedule.statusScheduled'),
    active: t('curatorSchedule.statusActive'),
    finished: t('curatorSchedule.statusFinished'),
    cancelled: t('curatorSchedule.statusCancelled'),
  } satisfies Record<LiveLesson['status'], string>;
}

// Shared marathon-dropdown options for the curator's create/bulk/event modals.
// Replaces the raw "Marathon ID" UUID text inputs (design spec §3: UUID-фильтры → выпадашки с названиями).
//
// BUG 1 fix: source the options from the curator's OWN managed marathons
// (GET /curator/marathons — those with active enrollments assigned to this
// curator) instead of the whole published catalog. Each label carries the
// active-student count so the curator never schedules a lesson onto a marathon
// with no audience; a marathon with 0 students is still listed but annotated.
function useMarathonOptions() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['curator', 'marathon-options'],
    queryFn: () => curatorApi.listMyMarathons(),
  });
  const items = q.data?.items ?? [];
  const options = items.map((m) => {
    const count =
      m.student_count > 0
        ? t('curatorSchedule.studentCount', { count: m.student_count })
        : t('curatorSchedule.noStudents');
    return { value: m.marathon_id, label: `${m.title} (${count})` };
  });
  // Plain title (no student-count suffix) for rendering audience labels.
  const titleById: Record<string, string> = {};
  items.forEach((m) => { titleById[m.marathon_id] = m.title; });
  return { options, isLoading: q.isLoading, titleById };
}

export function CuratorSchedulePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const LESSON_STATUS_LABELS = useLessonStatusLabels();
  const qc = useQueryClient();
  const { titleById: marathonTitles } = useMarathonOptions();
  const marathonName = (id?: string | null) =>
    (id && marathonTitles[id]) || undefined;
  const lessons = useQuery({ queryKey: ['curator', 'live-lessons'], queryFn: calendarApi.listMyLiveLessons });
  const events = useQuery({ queryKey: ['curator', 'calendar-events'], queryFn: calendarApi.listCuratorPlatformEvents });
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [activeAttendance, setActiveAttendance] = useState<{ code: string; lessonId: string } | null>(null);
  const [summary, setSummary] = useState<{ lessonId: string; title: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CalendarEvent | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const startAttendance = useMutation({
    mutationFn: (lessonId: string) => attendanceApi.startAttendance(lessonId, 60),
    onError: (e: unknown) => setActionErr(humanizeError(e)),
  });

  const deleteEvent = useMutation({
    mutationFn: (id: string) => calendarApi.deleteCuratorPlatformEvent(id),
    onSuccess: () => {
      setActionErr(null);
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ['curator', 'calendar-events'] });
    },
    onError: (e: unknown) => setActionErr(humanizeError(e)),
  });

  const refreshEvents = () => qc.invalidateQueries({ queryKey: ['curator', 'calendar-events'] });

  const isMobile = useIsMobile();
  const [tab, setTab] = useState<'lessons' | 'events'>('lessons');
  const [addSheet, setAddSheet] = useState(false);
  const [eventSheet, setEventSheet] = useState<CalendarEvent | null>(null);
  const markAttendance = async (lessonId: string) => {
    try {
      const r = await startAttendance.mutateAsync(lessonId);
      setActiveAttendance({ code: r.code, lessonId });
    } catch {
      // Error surfaced via startAttendance.onError (actionErr banner).
    }
  };

  // Окна — одни на обе раскладки (на телефоне они во весь экран, `phoneFull` внутри каждого).
  const modals = (
    <>
      {createOpen && <CreateLessonModal onClose={() => setCreateOpen(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['curator', 'live-lessons'] })} />}
      {bulkOpen && <BulkLessonsModal onClose={() => setBulkOpen(false)} onCreated={() => qc.invalidateQueries({ queryKey: ['curator', 'live-lessons'] })} />}
      {eventOpen && (
        <PlatformEventModal
          existing={editingEvent}
          onClose={() => { setEventOpen(false); setEditingEvent(null); }}
          onSaved={() => { refreshEvents(); setEventOpen(false); setEditingEvent(null); }}
        />
      )}
      {pendingDelete && (
        <Modal open onClose={() => setPendingDelete(null)} title={t('curatorSchedule.deleteEventTitle')} size="sm">
          <p className="text-sm text-on-surface-variant">
            {t('curatorSchedule.deleteEventConfirm', { title: pendingDelete.title })}
          </p>
          {actionErr && <p className="mt-2 text-xs text-error">{actionErr}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>{t('common.cancel')}</Button>
            <Button variant="danger" loading={deleteEvent.isPending} onClick={() => deleteEvent.mutate(pendingDelete.id)}>
              {t('common.delete')}
            </Button>
          </div>
        </Modal>
      )}
      {activeAttendance && (
        <Modal open onClose={() => setActiveAttendance(null)} title={t('curatorSchedule.attendanceCodeTitle')} size="sm">
          <p className="mb-3 text-sm text-on-surface-variant">{t('curatorSchedule.attendanceCodeHint')}</p>
          <p className="text-center font-display text-6xl font-extrabold tracking-display text-primary">{activeAttendance.code}</p>
          <Button fullWidth className="mt-4" variant="ghost" onClick={async () => {
            try {
              await attendanceApi.stopAttendance(activeAttendance.lessonId);
              setActiveAttendance(null);
            } catch (e) {
              setActionErr(humanizeError(e));
              setActiveAttendance(null);
            }
          }}>
            {t('curatorSchedule.stopAttendance')}
          </Button>
        </Modal>
      )}
      {summary && (
        <DoubleAttendanceModal lessonId={summary.lessonId} title={summary.title} onClose={() => setSummary(null)} />
      )}
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.11) ─────────────────────────
  // «+» → лист выбора (событие, живой урок, на неделю) вместо трёх кнопок в шапке, которые на 390 не помещались;
  // уроки и события — вкладками. Правки и отмены живого урока нет и на компьютере — не добавляем (§4.11 «Не делаем»).
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('curatorSchedule.title')}
          subtitle={t('curatorSchedule.subtitle')}
          right={<MAdd label={t('curatorSchedule.addLesson')} onClick={() => setAddSheet(true)} />}
        />
        <MSegmented
          tabs={[
            { key: 'lessons', label: t('staffMobile.scheduleLessons') },
            { key: 'events', label: t('staffMobile.scheduleEvents') },
          ]}
          active={tab}
          onChange={setTab}
        />
        {actionErr && <p className="px-0.5 text-[13px] text-error">{actionErr}</p>}

        {tab === 'lessons' ? (
          lessons.isLoading ? (
            <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
          ) : lessons.isError ? (
            <p className="px-0.5 text-[13px] text-error">{humanizeError(lessons.error)}</p>
          ) : !lessons.data?.length ? (
            <MEmpty icon="event" title={t('curatorSchedule.noLessons')} />
          ) : (
            <MBlock>
              {lessons.data.map((l, i, arr) => {
                const when = new Date(l.scheduled_at);
                return (
                  <MRow
                    key={l.id}
                    wrap
                    leading={
                      <span className="grid w-11 flex-none place-items-center rounded-xl bg-surface-container-high py-1 text-center leading-none">
                        <span className="font-display text-[17px] font-bold text-on-surface">{when.getDate()}</span>
                        <span className="text-[9.5px] uppercase text-on-surface-variant">
                          {when.toLocaleDateString('ru-RU', { month: 'short' })}
                        </span>
                      </span>
                    }
                    title={l.title}
                    meta={[
                      when.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                      l.topic,
                      t('curatorSchedule.durationMin', { min: l.duration_min }),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    aside={<MTag tone={l.status === 'active' ? 'warm' : 'muted'}>{LESSON_STATUS_LABELS[l.status] ?? l.status}</MTag>}
                    below={
                      <div className="flex flex-wrap gap-2 pb-3 pl-14">
                        {/* Вход в комнату — только когда видео включено (настройка video), как на компьютере. */}
                        {isFeatureOn('video') && (
                          <Button size="md" onClick={() => navigate(`/curator/live/${l.id}`)}>
                            <span className="material-symbols-outlined">videocam</span>
                            {t('curatorSchedule.joinRoom')}
                          </Button>
                        )}
                        <Button size="md" variant="secondary" onClick={() => markAttendance(l.id)}>
                          <span className="material-symbols-outlined">how_to_reg</span>
                          {t('curatorSchedule.markAttendance')}
                        </Button>
                        <Button size="md" variant="secondary" onClick={() => setSummary({ lessonId: l.id, title: l.title })}>
                          <span className="material-symbols-outlined">fact_check</span>
                          {t('curatorSchedule.attendanceSummary')}
                        </Button>
                        {l.meet_link && (
                          <a href={l.meet_link} target="_blank" rel="noreferrer">
                            <Button size="md" variant="ghost">Meet</Button>
                          </a>
                        )}
                      </div>
                    }
                    last={i === arr.length - 1}
                  />
                );
              })}
            </MBlock>
          )
        ) : events.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : events.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(events.error)}</p>
        ) : !events.data?.length ? (
          <MEmpty icon="campaign" title={t('curatorSchedule.noEvents')} />
        ) : (
          <MBlock>
            {events.data.map((e, i, arr) => (
              <MRow
                key={e.id}
                wrap
                onClick={() => { setEditingEvent(e); setEventOpen(true); }}
                leading={<MIcon icon="campaign" tone="warm" />}
                title={e.title}
                meta={[
                  t(EVENT_TYPE_KEYS[e.type] ?? e.type),
                  new Date(e.start_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
                  audienceLabel(e, t, marathonName),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                aside={
                  <button
                    type="button"
                    onClick={() => setEventSheet(e)}
                    aria-label={t('nav.more')}
                    className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
                  >
                    <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                  </button>
                }
                last={i === arr.length - 1}
              />
            ))}
          </MBlock>
        )}

        <ActionSheet
          open={addSheet}
          onClose={() => setAddSheet(false)}
          actions={[
            { label: t('curatorSchedule.addEvent'), icon: 'campaign', onSelect: () => { setEditingEvent(null); setEventOpen(true); } },
            { label: t('curatorSchedule.addLesson'), icon: 'videocam', onSelect: () => setCreateOpen(true) },
            { label: t('curatorSchedule.forWeek'), icon: 'date_range', onSelect: () => setBulkOpen(true) },
          ]}
        />
        <ActionSheet
          open={!!eventSheet}
          onClose={() => setEventSheet(null)}
          title={eventSheet?.title}
          actions={
            eventSheet
              ? [
                  { label: t('common.edit'), icon: 'edit', onSelect: () => { setEditingEvent(eventSheet); setEventOpen(true); } },
                  // То же окно подтверждения, что на компьютере: «Событие будет удалено у всех учеников».
                  { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => { setActionErr(null); setPendingDelete(eventSheet); } },
                ]
              : []
          }
        />
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('curatorSchedule.title')}
        subtitle={t('curatorSchedule.subtitle')}
        actions={
          <>
            <Button variant="secondary" onClick={() => { setEditingEvent(null); setEventOpen(true); }}>
              <span className="material-symbols-outlined">campaign</span>
              {t('curatorSchedule.addEvent')}
            </Button>
            <Button variant="secondary" onClick={() => setBulkOpen(true)}>
              <span className="material-symbols-outlined">date_range</span>
              {t('curatorSchedule.forWeek')}
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <span className="material-symbols-outlined">add</span>
              {t('curatorSchedule.addLesson')}
            </Button>
          </>
        }
      />

      {/* Platform events the curator created for their students (TZ §5.8) */}
      <Card className="mb-4">
        <CardTitle>{t('curatorSchedule.platformEvents')}</CardTitle>
        {events.isLoading ? (
          <p className="mt-2 text-sm text-on-surface-variant">{t('common.loading')}</p>
        ) : events.isError ? (
          <p className="mt-2 text-sm text-error">{humanizeError(events.error)}</p>
        ) : !events.data?.length ? (
          <p className="mt-2 text-sm text-on-surface-variant">{t('curatorSchedule.noEvents')}</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {events.data.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-button bg-surface-container-low p-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Pill variant="warning">{t(EVENT_TYPE_KEYS[e.type] ?? e.type)}</Pill>
                    <span className="font-medium text-on-surface">{e.title}</span>
                  </div>
                  <p className="mt-1 text-xs text-on-surface-variant">
                    {new Date(e.start_at).toLocaleString('ru-RU')} · {audienceLabel(e, t, marathonName)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => { setEditingEvent(e); setEventOpen(true); }}>{t('common.edit')}</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setActionErr(null); setPendingDelete(e); }}>{t('common.delete')}</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {actionErr && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{actionErr}</Card>
      )}

      {lessons.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : lessons.isError ? (
        <Card variant="subtle" className="text-center text-error">{humanizeError(lessons.error)}</Card>
      ) : !lessons.data?.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">{t('curatorSchedule.noLessons')}</Card>
      ) : (
        <ul className="space-y-3">
          {lessons.data.map((l) => (
            <li key={l.id}>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle>{l.title}</CardTitle>
                    {l.topic && <p className="mt-1 text-sm text-on-surface-variant">{l.topic}</p>}
                    <p className="mt-2 text-xs text-on-surface-variant">
                      {new Date(l.scheduled_at).toLocaleString('ru-RU')} · {t('curatorSchedule.durationMin', { min: l.duration_min })}
                    </p>
                    <Pill variant={l.status === 'active' ? 'success' : 'neutral'} className="mt-2">
                      {LESSON_STATUS_LABELS[l.status] ?? l.status}
                    </Pill>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Вход в комнату — только когда видео включено: сервер
                        видеоконференций сейчас не работает (настройка video). */}
                    {isFeatureOn('video') && (
                      <Button variant="primary" size="sm" onClick={() => navigate(`/curator/live/${l.id}`)}>
                        <span className="material-symbols-outlined">videocam</span>
                        {t('curatorSchedule.joinRoom')}
                      </Button>
                    )}
                    <Button variant="secondary" size="sm" onClick={() => markAttendance(l.id)}>
                      <span className="material-symbols-outlined">how_to_reg</span>
                      {t('curatorSchedule.markAttendance')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setSummary({ lessonId: l.id, title: l.title })}>
                      <span className="material-symbols-outlined">fact_check</span>
                      {t('curatorSchedule.attendanceSummary')}
                    </Button>
                    {l.meet_link && (
                      <a href={l.meet_link} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost">Meet</Button>
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {modals}
    </>
  );
}

// DoubleAttendanceModal — the two-signal attendance table for a live lesson:
// per student, whether they joined the room and how many roll-calls they
// answered, plus their overall status.
function DoubleAttendanceModal({ lessonId, title, onClose }: { lessonId: string; title: string; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['curator', 'attendance', 'double', lessonId],
    queryFn: () => attendanceApi.getDoubleAttendance(lessonId),
  });
  const rows = q.data?.rows ?? [];
  const total = q.data?.roll_call_total ?? 0;
  const isMobile = useIsMobile();

  const statusPill = (s: string) => {
    const variant = s === 'present' ? 'success' : s === 'late' ? 'warning' : s === 'excused' ? 'neutral' : 'danger';
    return <Pill variant={variant as 'success' | 'warning' | 'neutral' | 'danger'}>{t(`attendanceStatus.${s}`, s)}</Pill>;
  };

  return (
    <Modal open onClose={onClose} title={title} size="lg" phoneFull>
      <p className="mb-3 text-sm text-on-surface-variant">
        {t('curatorSchedule.rollCallsHeld', { count: total })}
      </p>
      {q.isLoading ? (
        <p className="py-6 text-center text-sm text-on-surface-variant">{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-on-surface-variant">{t('curatorSchedule.noAttendanceYet')}</p>
      ) : isMobile ? (
        // Телефон: студенты строками, «Зашёл · Перекличка x/N · статус» (§4.11, правило 9) — таблица на 4 колонки на
        // 390 уезжала вбок.
        <ul data-attendance-rows className="divide-y divide-outline">
          {rows.map((r) => {
            const missed = total > 0 && r.roll_call_answered === 0;
            const partial = total > 0 && r.roll_call_answered > 0 && r.roll_call_answered < total;
            return (
              <li key={r.student_id} className="flex items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-on-surface">{r.student_name || r.student_id.slice(0, 8)}</p>
                  <p className="flex items-center gap-1 text-[12px] text-on-surface-variant">
                    <span className={`material-symbols-outlined text-[15px] ${r.auto_joined ? 'text-success' : 'text-error'}`}>
                      {r.auto_joined ? 'check_circle' : 'cancel'}
                    </span>
                    {t('curatorSchedule.colJoined')} · {t('curatorSchedule.colRollCall')}{' '}
                    <span className={missed ? 'font-semibold text-error' : partial ? 'font-semibold text-warning' : ''}>
                      {total === 0 ? '—' : `${r.roll_call_answered}/${total}`}
                    </span>
                  </p>
                </div>
                {statusPill(r.status)}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-outline-variant/40 text-left text-xs uppercase tracking-label text-on-surface-variant">
                <th className="py-2 pr-2">{t('curatorSchedule.colStudent')}</th>
                <th className="px-2 py-2 text-center">{t('curatorSchedule.colJoined')}</th>
                <th className="px-2 py-2 text-center">{t('curatorSchedule.colRollCall')}</th>
                <th className="py-2 pl-2 text-right">{t('curatorSchedule.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const missed = total > 0 && r.roll_call_answered === 0;
                const partial = total > 0 && r.roll_call_answered > 0 && r.roll_call_answered < total;
                return (
                  <tr key={r.student_id} className="border-b border-outline-variant/20">
                    <td className="py-2 pr-2 font-medium text-on-surface">
                      {r.student_name || r.student_id.slice(0, 8)}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`material-symbols-outlined text-base ${r.auto_joined ? 'text-success' : 'text-error'}`}>
                        {r.auto_joined ? 'check_circle' : 'cancel'}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {total === 0 ? (
                        <span className="text-on-surface-variant">—</span>
                      ) : (
                        <span className={missed ? 'font-semibold text-error' : partial ? 'font-semibold text-warning' : 'text-success'}>
                          {r.roll_call_answered}/{total}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pl-2 text-right">{statusPill(r.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function audienceLabel(
  e: CalendarEvent,
  t: (key: string, opts?: Record<string, unknown>) => string,
  marathonName?: (id?: string | null) => string | undefined,
): string {
  switch (e.audience_type) {
    case 'marathon': {
      const name = marathonName?.(e.marathon_id);
      return name ? t('curatorSchedule.audienceCourseNamed', { name }) : t('curatorSchedule.audienceCourse');
    }
    case 'group': return t('curatorSchedule.audienceGroup');
    // В приложении ни слова «тариф» (решение владельца 13.10.2026): событие «по тарифу» заводят на сайте, здесь адресат
    // не подписываем.
    case 'tariff':
      if (IS_APP_BUILD) return '';
      return e.tariff ? t('curatorSchedule.audienceTariffNamed', { tariff: e.tariff }) : t('curatorSchedule.audienceTariff');
    default: return t('curatorSchedule.audienceAll');
  }
}

function toLocalInput(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
}

// Platform event create/edit (TZ §5.8): title, description, datetime, type,
// targeting and an optional click-through link.
function PlatformEventModal({
  existing,
  onClose,
  onSaved,
}: {
  existing: CalendarEvent | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Куратор адресует событие только своим группам (28.09.2026, lib/eventAudience); страница общая с админом.
  const role = useAuthStore((s) => s.user?.role);
  const isCurator = role === 'curator';
  const [form, setForm] = useState<PlatformEventInput & { description: string }>({
    title: existing?.title ?? '',
    description: existing?.description ?? '',
    start_at: toLocalInput(existing?.start_at) || toLocalInput(new Date().toISOString()),
    type: (existing?.type as PlatformEventType) ?? 'announcement',
    link: existing?.link ?? '',
    audience_type: initialAudience(role, existing?.audience_type),
    marathon_id: existing?.marathon_id ?? '',
    group_id: existing?.group_id ?? '',
    tariff: existing?.tariff ?? '',
  });

  const { t } = useTranslation();
  const { options: marathonOptions, isLoading: marathonsLoading } = useMarathonOptions();
  const [err, setErr] = useState<string | null>(null);

  // Groups the curator leads — for the "group" audience picker.
  const curatorGroups = useQuery({
    queryKey: ['curator', 'groups'],
    queryFn: () => usersApi.myCuratorGroups(),
  });
  const groupOptions = (curatorGroups.data?.items ?? []).map((g) => ({ value: g.id, label: g.name }));
  // Куратору группа выбрана сразу — первая своя; выбранная чужая (старое событие) заменяется своей.
  const groupId = isCurator ? effectiveGroupId(form.group_id, groupOptions.map((g) => g.value)) : form.group_id;
  const noGroups = isCurator && curatorGroups.isSuccess && groupOptions.length === 0;

  const save = useMutation({
    mutationFn: () => {
      const startIso = new Date(form.start_at).toISOString();
      const payload: PlatformEventInput = {
        title: form.title,
        description: form.description,
        start_at: startIso,
        end_at: startIso,
        type: form.type,
        link: form.link || undefined,
        audience_type: form.audience_type,
        marathon_id: form.audience_type === 'marathon' ? form.marathon_id || undefined : undefined,
        group_id: form.audience_type === 'group' ? groupId || undefined : undefined,
        tariff: form.audience_type === 'tariff' ? form.tariff || undefined : undefined,
      };
      return existing
        ? calendarApi.updateCuratorPlatformEvent(existing.id, payload)
        : calendarApi.createCuratorPlatformEvent(payload);
    },
    onSuccess: onSaved,
    onError: (e: unknown) => {
      const key = audienceRefusalKey(apiErrorCode(e));
      setErr(key ? t(key) : humanizeError(e));
    },
  });

  return (
    <Modal open onClose={onClose} title={existing ? t('curatorSchedule.editEvent') : t('curatorSchedule.newEvent')} size="lg" phoneFull>
      <div className="space-y-3">
        <Input label={t('curatorSchedule.eventTitle')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Input label={t('curatorSchedule.eventDescription')} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Input type="datetime-local" label={t('curatorSchedule.eventWhen')} value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
        <Select
          label={t('curatorSchedule.eventType')}
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value as PlatformEventType })}
          options={[
            { value: 'webinar', label: t('curatorSchedule.typeWebinar') },
            { value: 'deadline', label: t('curatorSchedule.typeDeadline') },
            { value: 'exam', label: t('curatorSchedule.typeExam') },
            { value: 'announcement', label: t('curatorSchedule.typeAnnouncement') },
          ]}
        />
        <Select
          label={t('curatorSchedule.eventAudience')}
          value={form.audience_type}
          onChange={(e) => setForm({ ...form, audience_type: e.target.value as PlatformEventInput['audience_type'] })}
          options={[
            { value: 'all', label: t('curatorSchedule.audienceAll') },
            { value: 'marathon', label: t('curatorSchedule.audienceCourse') },
            { value: 'group', label: t('curatorSchedule.audienceGroup') },
            // ⚠️ Аудитория «по тарифу» и названия тарифов — только сайт (26.09.2026, сборка 61): в приложении
            // из магазина слова Premium/Standard/Individual не должно быть даже у куратора. Ветка сборки, а не
            // isStoreBuild(): строки выпадают из dist-mobile, а не прячутся во время работы.
            ...(IS_APP_BUILD ? [] : [{ value: 'tariff', label: t('curatorSchedule.audienceTariff') }]),
            // Куратору — только «Группе» (28.09.2026, lib/eventAudience).
          ].filter((o) => audienceOptions(role).includes(o.value as EventAudience))}
        />
        {form.audience_type === 'group' && (
          <Select
            label={t('curatorSchedule.audienceGroup')}
            value={groupId ?? ''}
            onChange={(e) => setForm({ ...form, group_id: e.target.value })}
            placeholder={curatorGroups.isLoading ? '…' : t('curatorSchedule.selectGroup')}
            options={groupOptions}
          />
        )}
        {noGroups && <p className="text-xs text-on-surface-variant">{t('curatorSchedule.noGroupsHint')}</p>}
        {form.audience_type === 'marathon' && (
          <Select
            label={t('curatorSchedule.course')}
            value={form.marathon_id}
            onChange={(e) => setForm({ ...form, marathon_id: e.target.value })}
            placeholder={marathonsLoading ? t('curatorSchedule.loadingCourses') : t('curatorSchedule.selectCourse')}
            options={marathonOptions}
          />
        )}
        {IS_APP_BUILD ? null : form.audience_type === 'tariff' && (
          <Select
            label={t('curatorSchedule.tariff')}
            value={form.tariff ?? ''}
            onChange={(e) => setForm({ ...form, tariff: e.target.value })}
            options={[
              { value: 'Standard', label: 'Standard' },
              { value: 'Premium', label: 'Premium' },
              { value: 'Individual', label: 'Individual' },
            ]}
          />
        )}
        <Input label={t('curatorSchedule.eventLink')} value={form.link ?? ''} onChange={(e) => setForm({ ...form, link: e.target.value })} />
        {err && <p className="text-xs text-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!canSaveEvent({ ...form, audience_type: form.audience_type as EventAudience, group_id: groupId })}
          >
            {existing ? t('common.save') : t('curatorSchedule.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateLessonModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ marathon_id: '', title: '', topic: '', scheduled_at: '', duration_min: 60 });
  const { options: marathonOptions, isLoading: marathonsLoading } = useMarathonOptions();
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => calendarApi.createLiveLesson({ ...data, scheduled_at: new Date(data.scheduled_at).toISOString() } as Partial<LiveLesson>),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: unknown) => setErr(humanizeError(e)),
  });

  const canSubmit = !!data.marathon_id && !!data.title && !!data.scheduled_at;

  return (
    <Modal open onClose={onClose} title={t('curatorSchedule.newLiveLesson')} phoneFull>
      <div className="space-y-3">
        <Select
          label={t('curatorSchedule.course')}
          value={data.marathon_id}
          onChange={(e) => setData({ ...data, marathon_id: e.target.value })}
          placeholder={marathonsLoading ? t('curatorSchedule.loadingCourses') : t('curatorSchedule.selectCourse')}
          options={marathonOptions}
        />
        <Input label={t('curatorSchedule.lessonTitle')} value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
        <Input label={t('curatorSchedule.lessonTopic')} value={data.topic} onChange={(e) => setData({ ...data, topic: e.target.value })} />
        <Input type="datetime-local" label={t('curatorSchedule.eventWhen')} value={data.scheduled_at} onChange={(e) => setData({ ...data, scheduled_at: e.target.value })} />
        <Select label={t('curatorSchedule.duration')} value={String(data.duration_min)} onChange={(e) => setData({ ...data, duration_min: Number(e.target.value) })} options={[
          { value: '30', label: t('curatorSchedule.min', { n: 30 }) },
          { value: '45', label: t('curatorSchedule.min', { n: 45 }) },
          { value: '60', label: t('curatorSchedule.min', { n: 60 }) },
          { value: '90', label: t('curatorSchedule.min', { n: 90 }) },
        ]} />
        {err && <p className="text-xs text-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSubmit}>{t('curatorSchedule.create')}</Button>
        </div>
      </div>
    </Modal>
  );
}

function BulkLessonsModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState({ marathon_id: '', title: '', topic: '', start_date: '', week_count: 4, days_of_week: [1, 3, 5] as number[], duration_min: 60 });
  const { options: marathonOptions, isLoading: marathonsLoading } = useMarathonOptions();
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      calendarApi.bulkCreateLiveLessons({
        ...data,
        // <input type="date"> yields "YYYY-MM-DD"; the backend binds start_date
        // as a Go time.Time which requires a full RFC3339 timestamp.
        start_date: data.start_date ? new Date(data.start_date).toISOString() : '',
      }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e: unknown) => setErr(humanizeError(e)),
  });

  const canSubmit = !!data.marathon_id && !!data.title && !!data.start_date && data.days_of_week.length > 0;

  const toggleDay = (d: number) => {
    setData((s) => ({
      ...s,
      days_of_week: s.days_of_week.includes(d) ? s.days_of_week.filter((x) => x !== d) : [...s.days_of_week, d],
    }));
  };

  return (
    <Modal open onClose={onClose} title={t('curatorSchedule.bulkTitle')} size="lg" phoneFull>
      <div className="space-y-3">
        <Select
          label={t('curatorSchedule.course')}
          value={data.marathon_id}
          onChange={(e) => setData({ ...data, marathon_id: e.target.value })}
          placeholder={marathonsLoading ? t('curatorSchedule.loadingCourses') : t('curatorSchedule.selectCourse')}
          options={marathonOptions}
        />
        <Input label={t('curatorSchedule.lessonTitle')} value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
        <Input label={t('curatorSchedule.lessonTopic')} value={data.topic} onChange={(e) => setData({ ...data, topic: e.target.value })} />
        <Input type="date" label={t('curatorSchedule.startDate')} value={data.start_date} onChange={(e) => setData({ ...data, start_date: e.target.value })} />
        <Input type="number" label={t('curatorSchedule.weeks')} value={data.week_count} onChange={(e) => setData({ ...data, week_count: Number(e.target.value) })} />
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-label text-on-surface-variant">{t('curatorSchedule.daysOfWeek')}</p>
          <div className="flex flex-wrap gap-2">
            {[t('curatorSchedule.daySun'), t('curatorSchedule.dayMon'), t('curatorSchedule.dayTue'), t('curatorSchedule.dayWed'), t('curatorSchedule.dayThu'), t('curatorSchedule.dayFri'), t('curatorSchedule.daySat')].map((d, i) => (
              <button
                key={d}
                onClick={() => toggleDay(i)}
                // На телефоне — 44 px, норма касания (max-md); на компьютере прежние.
                className={`rounded-button px-3 py-1.5 text-sm max-md:min-h-11 max-md:min-w-11 ${data.days_of_week.includes(i) ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-on-surface'}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        {err && <p className="text-xs text-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSubmit}>{t('curatorSchedule.createBulk', { count: data.week_count * data.days_of_week.length })}</Button>
        </div>
      </div>
    </Modal>
  );
}
